/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

// Init Bootstrap tooltips
document.querySelectorAll('[data-bs-toggle="tooltip"]')
  .forEach((el) => new window.bootstrap.Tooltip(el))

// Init Bootstrap popovers
document.querySelectorAll('[data-bs-toggle="popover"]')
  .forEach((el) => new window.bootstrap.Popover(el))

window.theme = window.theme || {}

// Debouce
window.theme.debounce = function (callback, wait = 200) {
  let timeout
  return (...args) => {
    const context = this
    clearTimeout(timeout)
    timeout = setTimeout(() => callback.apply(context, args), wait)
  }
}

// Throttle
window.theme.throttle = function (callback, timeFrame = 200) {
  let lastTime = 0
  return function () {
    const now = Date.now()
    if (now - lastTime >= timeFrame) {
      callback()
      lastTime = now
    }
  }
}

// Create cookie
window.theme.createCookie = function (name, value, days) {
  let date, expires
  if (days) {
    date = new Date()
    date.setDate(date.getDate() + days)
    expires = '; expires=' + date.toUTCString()
  } else {
    expires = ''
  }
  document.cookie = name + '=' + value + expires + '; path=/'
}

// Detect scroll
window.addEventListener('scroll', () => {
  if (window.scrollY > 0) {
    document.documentElement.classList.add('has-scrolled')
  } else {
    document.documentElement.classList.remove('has-scrolled')
  }
})

// Calculate "xx time ago"
window.theme.calcTimeAgo = function (timestamp) {
  const now = new Date().getTime()
  const diff = now - timestamp

  let text

  if (diff < 60000) {
    text = window.theme.locales.times.moments
  } else if (diff < 3.6e+6) {
    const min = Math.round((diff) / 60000)
    text = min === 1
      ? `${min} ${window.theme.locales.times.minute}`
      : `${min} ${window.theme.locales.times.minutes}`
  } else if (diff < 8.64e+7) {
    const hours = Math.round((diff) / 3.6e+6)
    text = hours === 1
      ? `${hours} ${window.theme.locales.times.hour}`
      : `${hours} ${window.theme.locales.times.hours}`
  } else {
    const days = Math.round((diff) / 8.64e+7)
    text = days === 1
      ? `${days} ${window.theme.locales.times.day}`
      : `${days} ${window.theme.locales.times.days}`
  }

  return `${text} ${window.theme.locales.times.ago}`
}

// Format money
window.Shopify.formatMoney = function (cents, moneyFormat = window.Shopify.moneyFormat) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '')
  }

  let value = ''
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/

  function defaultOption (opt, def) {
    return (typeof opt === 'undefined' ? def : opt)
  }

  function formatWithDelimiters (number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2)
    thousands = defaultOption(thousands, ',')
    decimal = defaultOption(decimal, '.')

    if (isNaN(number) || number == null) {
      return 0
    }

    number = (number / 100.0).toFixed(precision)

    const parts = number.split('.')
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
    const cents = parts[1] ? (decimal + parts[1]) : ''

    return dollars + cents
  }

  switch (moneyFormat.match(placeholderRegex)[1]) {
  case 'amount':
    value = formatWithDelimiters(cents, 2)
    break
  case 'amount_no_decimals':
    value = formatWithDelimiters(cents, 0)
    break
  case 'amount_with_comma_separator':
    value = formatWithDelimiters(cents, 2, '.', ',')
    break
  case 'amount_no_decimals_with_comma_separator':
    value = formatWithDelimiters(cents, 0, '.', ',')
    break
  }

  return moneyFormat.replace(placeholderRegex, value)
}

window.theme.formatMoneyWithoutTrailingZeros = function (amount) {
  if (amount == null || Number.isNaN(amount)) {
    return ''
  }

  if (!window.Shopify || typeof window.Shopify.formatMoney !== 'function') {
    const fallback = Number(amount) / 100
    if (Number.isNaN(fallback)) {
      return ''
    }
    const stringValue = fallback.toFixed(2)
    return stringValue.endsWith('.00') ? stringValue.slice(0, -3) : stringValue
  }

  const formatted = window.Shopify.formatMoney(amount)
  if (typeof formatted !== 'string') {
    return formatted || ''
  }

  const decimalMatch = formatted.match(/([.,]\d+)(?![\d])/)
  if (!decimalMatch) {
    return formatted
  }

  const decimals = decimalMatch[0].slice(1)
  if (/^0+$/.test(decimals)) {
    return formatted.replace(decimalMatch[0], '')
  }

  return formatted
}

window.theme.afterpayMinAmount = 15 * 100
window.theme.afterpaySelector = '.afterpay-placement, .shopify-afterpay-message, square-placement'

window.theme.toggleAfterpayElement = function (element, shouldShow) {
  if (!element) {
    return
  }

  if (shouldShow) {
    element.classList.remove('afterpay-hidden')
    element.removeAttribute('hidden')
  } else {
    element.classList.add('afterpay-hidden')
    element.setAttribute('hidden', 'hidden')
  }
}

window.theme.updateAfterpayProductPlacement = function (priceCents, context) {
  const scope = context || document
  const priceWrapper = scope.querySelector?.('.product-price[data-afterpay-threshold]')
    || document.querySelector('.product-price[data-afterpay-threshold]')

  if (!priceWrapper) {
    return
  }

  if (priceCents == null) {
    priceWrapper.setAttribute('data-afterpay-hidden', 'true')
    window.theme.syncAfterpayPlacements(priceWrapper, false)
    return
  }

  const threshold = Number(priceWrapper.dataset.afterpayThreshold)
    || window.theme.afterpayMinAmount
    || 0
  const shouldShow = Number(priceCents) >= threshold

  if (shouldShow) {
    priceWrapper.removeAttribute('data-afterpay-hidden')
  } else {
    priceWrapper.setAttribute('data-afterpay-hidden', 'true')
  }

  window.theme.syncAfterpayPlacements(priceWrapper, shouldShow)
}

window.theme.updateAfterpayCartPlacement = function (totalCents) {
  if (totalCents == null) {
    return
  }

  const container = document.querySelector('.afterpay-dynamic-cart-placement')
  if (!container) {
    return
  }

  const threshold = Number(container.dataset.afterpayThreshold)
    || window.theme.afterpayMinAmount
    || 0
  const shouldShow = Number(totalCents) >= threshold

  if (shouldShow) {
    container.removeAttribute('data-afterpay-hidden')
  } else {
    container.setAttribute('data-afterpay-hidden', 'true')
  }

  container.querySelectorAll(window.theme.afterpaySelector)
    .forEach(node => window.theme.toggleAfterpayElement(node, shouldShow))
}

window.theme.syncAfterpayPlacements = function (priceWrapper, shouldShow) {
  if (!priceWrapper) {
    return
  }

  const placements = new Set()

  priceWrapper.querySelectorAll(window.theme.afterpaySelector)
    .forEach(node => placements.add(node))

  const siblingPlacements = priceWrapper.parentElement
    ?.querySelectorAll(window.theme.afterpaySelector) || []
  siblingPlacements.forEach(node => {
    if (node.closest('.product-price') === priceWrapper || node.previousElementSibling === priceWrapper) {
      placements.add(node)
    }
  })

  if (priceWrapper.nextElementSibling?.matches?.(window.theme.afterpaySelector)) {
    placements.add(priceWrapper.nextElementSibling)
  }

  placements.forEach(node => window.theme.toggleAfterpayElement(node, shouldShow))
}

window.theme.handleAfterpayPlacement = function (element) {
  if (!element || !(element instanceof HTMLElement)) {
    return
  }

  const placement = element.matches(window.theme.afterpaySelector)
    ? element
    : element.querySelector?.(window.theme.afterpaySelector)

  if (!placement) {
    return
  }

  let priceWrapper = placement.closest('.product-price[data-afterpay-threshold]')
  if (!priceWrapper) {
    const prev = placement.previousElementSibling?.closest?.('.product-price[data-afterpay-threshold]')
    if (prev) priceWrapper = prev
  }

  if (priceWrapper) {
    const hidden = priceWrapper.getAttribute('data-afterpay-hidden') === 'true'
    window.theme.toggleAfterpayElement(placement, !hidden)
  }

  const cartContainer = placement.closest('.afterpay-dynamic-cart-placement')
  if (cartContainer) {
    const hidden = cartContainer.getAttribute('data-afterpay-hidden') === 'true'
    window.theme.toggleAfterpayElement(placement, !hidden)
  }
}

window.theme.initAfterpayObserver = function () {
  if (window.theme.afterpayObserver || typeof MutationObserver === 'undefined') {
    return
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach(node => {
        if (!(node instanceof HTMLElement)) {
          return
        }
        window.theme.handleAfterpayPlacement(node)
      })
    })
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true
  })

  window.theme.afterpayObserver = observer

  document.querySelectorAll(window.theme.afterpaySelector)
    .forEach(node => window.theme.handleAfterpayPlacement(node))

  const cartContainer = document.querySelector('.afterpay-dynamic-cart-placement[data-cart-total]')
  if (cartContainer) {
    const total = Number(cartContainer.getAttribute('data-cart-total'))
    if (!Number.isNaN(total)) {
      window.theme.updateAfterpayCartPlacement(total)
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.theme.initAfterpayObserver()
})

// Resize images
window.Shopify.resizeImage = function (src, size, crop = '') {
  return src.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.')
    .replace(/\.jpg|\.png|\.gif|\.jpeg/g, (match) => {
      if (crop.length) {
        crop = `_crop_${crop}`
      }
      return `_${size}${crop}${match}`
    })
}

// Shopify's callenge page
document.querySelector('.btn.shopify-challenge__button')
  ?.classList.add('btn-primary')

// Shopify's errors messages
const errors = document.querySelector('.errors')
if (errors) {
  errors.classList.add('alert', 'alert-danger')
}

// Wrap Shopify's section apps within a container
document.querySelectorAll('.shopify-section > .shopify-app-block').forEach(elem => {
  elem.classList.add('container')
})

// Detect viewport with the Intersection Observer API
const observer = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add('viewport-entered')
    } else {
      entry.target.classList.remove('viewport-entered')
    }
  })
}, { threshold: 0.4 })

document.querySelectorAll('.viewport-detect').forEach((el) => {
  observer.observe(el)
})

// Swiper Slider
class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (!this.slider) return;

    this.slider.destroy(true, true);
    this.slider = null;
  }

  init() {
    if (this.slider || !window.Swiper) return;

    const swiperEl = this.querySelector(".swiper");
    if (!swiperEl) return;

    const slidesCount = swiperEl.querySelectorAll(
      ".swiper-slide:not(.swiper-slide-duplicate)",
    ).length;
    const slidesPerView = this.getSlidesPerView();

    // Only enable loop if we have more slides than can be shown at once
    const shouldLoop = this.dataset.loop === "true" && slidesCount > slidesPerView;

    this.slider = new window.Swiper(swiperEl, {
      speed: Number(this.dataset.sliderSpeed),
      autoplay: this.dataset.sliderAutoplay === "0"
        ? undefined
        : { delay: Number(this.dataset.sliderAutoplay) * 1000 },
      navigation: {
        enabled: this.dataset.sliderNavigation === "true",
        prevEl: ".swiper-button-prev",
        nextEl: ".swiper-button-next",
      },
      pagination: {
        enabled: this.dataset.sliderPagination === "true",
        el: ".swiper-pagination",
        type: "bullets",
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`;
        },
      },
      scrollbar: {
        enabled: this.dataset.sliderScrollbar === "true",
        el: this.querySelector(".swiper-scrollbar"),
        draggable: true,
      },
      breakpoints: this.getBreakpoints(),
      loop: shouldLoop,
      // Center slides when total slides are not enough to fill the viewport
      centerInsufficientSlides: true,
      watchOverflow: true,
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      on: {
        init: function () {
          this.navigation.update();
        },
        resize: function () {
          this.navigation.update();
        },
      },
    });
  }

  getSlidesPerView() {
    const width = window.innerWidth;
    if (width < 600) {
      return Number(this.dataset.breakpointMobile);
    } else if (width < 1200) {
      return Number(this.dataset.breakpointTablet);
    } else {
      return Number(this.dataset.breakpointDesktop);
    }
  }

  getBreakpoints() {
    const mobileSetting = Number(this.dataset.breakpointMobile) || 1;
    const tabletSetting = Number(this.dataset.breakpointTablet) || 2;
    const desktopSetting = Number(this.dataset.breakpointDesktop) || 4;
    const partialMobile = this.dataset.sliderPartialSlidesMobile === "true";

    const baseMobile = partialMobile ? Math.max(mobileSetting, 2) : mobileSetting;
    const mobileSlides = partialMobile ? baseMobile + 0.12 : Math.max(baseMobile, 1.4);
    const mobileWideSlides = partialMobile ? baseMobile + 0.32 : Math.max(baseMobile, 2);
    const tabletSlides = partialMobile ? Math.max(tabletSetting, baseMobile + 0.8) : Math.max(tabletSetting, 2.2);

    return {
      0: {
        slidesPerView: mobileSlides,
        centeredSlides: true,
      },
      460: {
        slidesPerView: mobileWideSlides,
        centeredSlides: true,
      },
      600: {
        slidesPerView: tabletSlides,
        centeredSlides: partialMobile ? false : true,
      },
      760: {
        slidesPerView: Math.max(tabletSetting, 3),
        centeredSlides: false,
      },
      1200: {
        slidesPerView: desktopSetting,
        centeredSlides: false,
      },
    };
  }
}
customElements.define('swiper-slider', SwiperSlider)

/*
  Localization form
*/
document.querySelectorAll('.shopify-localization-form button').forEach(btn => {
  btn.addEventListener('click', () => {
    btn.closest('form').querySelector('[name="country_code"]').value = btn.dataset.isoCode
    btn.closest('form').submit()
  })
})

/*
  Smooth anchor scrolling with header offset
*/
window.theme.scrollToAnchor = function (targetId, options = {}) {
  if (!targetId) return false

  const normalizedId = targetId.replace(/^#/, '')
  const targetEl = document.getElementById(normalizedId)

  if (!targetEl) return false

  const providedExtra = typeof options.extraOffset === 'number'
    ? options.extraOffset
    : (typeof options.extraPadding === 'number' ? options.extraPadding : undefined)

  const basePadding = Number.isFinite(providedExtra) ? providedExtra : 24

  let offset = Math.max(basePadding, 0)

  const header = document.querySelector('[data-navbar-root]') || document.getElementById('navbar-wrapper') || document.querySelector('[data-sticky-header]')
  if (header) {
    const headerStyles = window.getComputedStyle(header)
    const headerHeight = header.getBoundingClientRect().height || 0
    const marginBottom = parseFloat(headerStyles.marginBottom) || 0
    offset += headerHeight + marginBottom
  }

  const targetTop = window.scrollY + targetEl.getBoundingClientRect().top - offset

  window.scrollTo({
    top: targetTop,
    behavior: 'smooth',
  })

  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, '', `#${normalizedId}`)
  }

  return true
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-scroll-anchor]')
  if (!trigger) return

  const targetId = trigger.getAttribute('data-scroll-anchor')
  const extraOffsetAttr = trigger.getAttribute('data-scroll-offset')
  const extraOffset = extraOffsetAttr != null ? parseInt(extraOffsetAttr, 10) : undefined

  const scrolled = window.theme.scrollToAnchor(targetId, { extraOffset })
  if (scrolled) {
    event.preventDefault()
  }
})

document.addEventListener('DOMContentLoaded', () => {
  const hash = window.location.hash
  if (!hash || hash.length <= 1) return

  setTimeout(() => {
    window.theme.scrollToAnchor(hash.slice(1))
  }, 150)
})
